import {
  ProductionComments,
  ProductionImages,
  Productions,
  Tags,
} from '@/api/spotwrite';
import useConfig from '@/components/utils/Config';
import useFormatFunc from './Formatter';

export default function useProductionsFunc() {
  const config = useConfig();
  const formatter = useFormatFunc();

  // 第一回グランプリへのエントリーを希望するをチェックしたときのタグ
  const gpOneEntryTagName = '第一回グランプリ応募';
  // 第一回グランプリへのエントリーの対象工程
  const gpOneEntryFormName = '線画';

  function openViewer(item?: Productions) {
    if (item) {
      const idStr = item.id.toString();
      const urlTemplate = config.VIEWER_URL_TEMPLATE();
      const url = urlTemplate.replace(':workId', idStr);
      window.open(url);
    }
  }

  function imageCount(item?: Productions) {
    if (item && item._count) {
      return item._count.productionImages;
    }
    return 0;
  }

  function getImageUrl(image?: ProductionImages) {
    if (image?.imageUrl) {
      // const urlTemplate = config.IMAGE_URL_TEMPLATE();
      // return urlTemplate.replace(':image', image.image);
      return image?.imageUrl;
    }
    return undefined;
  }

  function getThumbUrl(image?: ProductionImages) {
    if (image?.thumbUrl) {
      // const urlTemplate = config.IMAGE_URL_TEMPLATE();
      // return urlTemplate.replace(':image', image.image);
      return image?.thumbUrl;
    }
    return undefined;
  }

  /**
   * 作品のカバー画像（サムネイル画像）を返します。画像が存在しない場合は undefined になります。
   * @param item
   * @returns
   */
  function getCoverImage(item?: Productions) {
    if (item && item.productionImages && item.productionImages.length > 0) {
      if (item.productionImages[0]) {
        return getThumbUrl(item.productionImages[0]);
      }
    }
    return undefined;
  }

  /**
   * 作品の1ページ目のオリジナル画像を返します。画像が存在しない場合は undefined になります。
   * @param item
   * @returns
   */
  function getFirstImage(item?: Productions) {
    if (item && item.productionImages && item.productionImages.length > 0) {
      if (item.productionImages[0]) {
        return getImageUrl(item.productionImages[0]);
      }
    }
    return undefined;
  }

  /**
   * ジャンルの名称をJOINして返します。
   * @param item
   * @returns
   */
  function getGenreNames(item?: Productions) {
    if (item && item.productionGenres && item.productionGenres.length > 0) {
      const titles: string[] = [];
      item.productionGenres.forEach((genre) => {
        if (genre.genre) {
          titles.push(genre.genre.name);
        }
      });
      return titles.join(', ');
    }
    return '';
  }

  /**
   * 作品のシリーズ名称を返します。
   * @param item
   * @returns
   */
  function getSeriesName(item?: Productions) {
    if (item && item.productionSeries && item.productionSeries.length > 0) {
      const titles: string[] = [];
      item.productionSeries.forEach((series) => {
        if (series.series) {
          titles.push(series.series.title);
        }
      });
      return titles.join(', ');
    }
    return '';
  }

  /**
   * シリーズが存在する場合は true
   * @param item
   * @returns
   */
  function hasSeries(item?: Productions) {
    if (item && item._count) {
      return item._count.productionSeries > 0;
    }
    return false;
  }

  /**
   * Tagを取り出して返します
   * @param item
   * @returns
   */
  function getTags(item?: Productions) {
    const tags: Tags[] = [];
    if (item && item.productionTags && item.productionTags.length > 0) {
      item.productionTags.forEach((rel) => {
        tags.push(rel.tag);
      });
    }
    return tags;
  }

  /**
   * Tagが1つ以上設定されていたら true
   * @param item
   * @returns
   */
  function hasTags(item?: Productions) {
    if (item && item._count) {
      return item._count.productionTags > 0;
    }
    return false;
  }

  /**
   * ProductionCommentsを取り出して返します
   * @param item
   * @returns
   */
  function getComments(item?: Productions) {
    const comments: ProductionComments[] = [];
    if (item && item.productionComments && item.productionComments.length > 0) {
      item.productionComments.forEach((comment) => {
        comments.push(comment);
      });
    }
    return comments;
  }

  /**
   *
   * @param item
   * @returns
   */
  function hasComments(item?: Productions) {
    if (item && item._count) {
      return item._count.productionComments > 0;
    }
    return false;
  }

  /**
   * 作品のコメント数を返します
   * @param item
   * @returns
   */
  function commentCount(item?: Productions) {
    if (item && item.commentsCount) {
      return item.commentsCount;
    }
    if (item && item._count) {
      return item._count.productionComments;
    }
    return 0;
  }

  /**
   * 作品のお気に入り数を返します
   * @param item
   * @returns
   */
  function favoriteCount(item?: Productions) {
    if (item && item.favoritesCount) {
      return item.favoritesCount;
    }
    if (item && item._count) {
      return item._count.favoriteProductions;
    }
    return 0;
  }

  /**
   * 作品のいいね数を返します
   * @param item
   * @returns
   */
  function goodCount(item?: Productions) {
    if (item && item.goodCount) {
      return item.goodCount;
    }
    if (item && item._count) {
      return item._count.goodProductions;
    }
    return 0;
  }

  /**
   * 作者のアイコン画像URLを返します
   * @param item
   * @returns
   */
  function getAuthorIconUrl(item?: Productions) {
    if (item && item.user && item.user.imageUrl) {
      const urlTemplate = config.IMAGE_URL_TEMPLATE();
      return urlTemplate.replace(':image', item.user.imageUrl);
    }
    return '';
  }

  /**
   * 作者のニックネームを返します。
   * @param item
   * @returns
   */
  function getAuthorNickname(item?: Productions) {
    if (item && item.user) {
      return item.user.nickname?.toString();
    }
    return '';
  }

  /**
   * 画像作品の場合はtrue
   * @param item
   * @returns
   */
  function isPictureWork(item?: Productions) {
    if (item && item.formId && item.formId === 1) {
      return false;
    }
    return true;
  }

  /**
   * 「ネーム」「線画」「カラーリング」工程の作品であるにもかかわらず、画像が存在していない場合はtrue
   * @param item
   * @returns
   */
  function hasImageError(item?: Productions) {
    if (isPictureWork(item) && (!item?.productionImages || item?.productionImages.length < 1)) {
      return true;
    }
    return false;
  }

  /**
   * 工程が「線画」で、タグに第一回グランプリ応募用のタグがついている場合にtrueを返します。
   * @param item
   */
  function isEnteredGpOne(item?: Productions) {
    if (item && item.form && item.form.title === gpOneEntryFormName) {
      const tags = getTags(item);
      if (tags && tags.some((tag) => (tag.name === gpOneEntryTagName))) {
        return true;
      }
    }
    return false;
  }

  /**
   * 投稿日時を返します
   * @param item
   * @returns
   */
  function getPostDateTime(item?: Productions) {
    if (item && item.createdAt) {
      return formatter.formatDate(item.createdAt, 'YYYY年MM月DD日 HH:mm');
    }
    return undefined;
  }

  return {
    getFirstImage,
    getCoverImage,
    getGenreNames,
    getSeriesName,
    hasSeries,
    getAuthorNickname,
    getAuthorIconUrl,
    isPictureWork,
    getTags,
    hasTags,
    getComments,
    hasComments,
    commentCount,
    favoriteCount,
    goodCount,
    openViewer,
    imageCount,
    hasImageError,
    isEnteredGpOne,
    getPostDateTime,
    gpOneEntryTagName,
    gpOneEntryFormName,
  };
}
